// react components
import { useState, useEffect, useMemo, useCallback } from "react";
// react-router-dom components

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// mui components
import { useTheme, styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";

//store
import { useSelector, useDispatch } from "react-redux";
import { loadCurrentFilterState } from "../../store/features/respondentDashboardSlice";

const Accordion = styled((props) => (
    <MuiAccordion
        disableGutters
        elevation={0}
        square
        {...props}
    />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
        borderBottom: 0,
    },
    "&::before": {
        display: "none",
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, .05)" : "rgba(0, 0, 0, .03)",
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
        transform: "rotate(90deg)",
    },
    "& .MuiAccordionSummary-content": {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const typeOfVariable = ["SCREENER", "RECALL", "ADDITIONAL_PRODUCT_PERFORMANCE"]; // Define order

function DashboardFilters({ height = 110, filterData = {}, settings = [] }) {
    const theme = useTheme();
    const dispatch = useDispatch();
    const selectedFilters = useSelector((state) => state.respondentDashboardStore.currentFilter) || {};
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const filterLabelsAndOptions = filterData?.filters || {};
    const backendVaribleToExclude = filterData?.add_pro_backend_variables || [];

    const filtersLabelMap = filterData?.mapping || {};

    const [isResetDisabled, setIsResetDisabled] = useState(true);
    const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);

    const memoizedFilters = useMemo(() => selectedFilters || {}, [selectedFilters]);

    const [filterOrder, setFilterOrder] = useState([]);

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const updateOrder = useCallback(() => {
        const ordered = typeOfVariable.flatMap(
            (type) =>
                settings
                    .filter((item) => item.type_of_variable === type) // Group by type_of_variable
                    .sort((a, b) => a.sequence_number - b.sequence_number) // Sort within each group
                    .map((a) => a.backend_variable) // Extract backend_variable
        );

        const _filterOrder = Object.keys(filterLabelsAndOptions);

        // Create a mapping of elements to their index in ordered
        const indexMap = new Map(ordered.map((value, index) => [value, index]));

        // Sort _filterOrder based on the indexMap
        const sortedSecondArray = _filterOrder.slice().sort((a, b) => {
            if (a === "product_codes") return -1; // 'product_codes' always first
            if (b === "product_codes") return 1;

            const indexA = indexMap.has(a) ? indexMap.get(a) : Infinity;
            const indexB = indexMap.has(b) ? indexMap.get(b) : Infinity;

            return indexA - indexB;
        });

        // Only set state if sortedSecondArray is different
        setFilterOrder((prevOrder) => {
            const _oldSortedArray = prevOrder.sort((a, b) => {
                a.backend_variable - b.backend_variable;
            });
            const _newSortedArray = sortedSecondArray.sort((a, b) => {
                a.backend_variable - b.backend_variable;
            });
            // Compare previous order to new order
            if (JSON.stringify(_oldSortedArray) !== JSON.stringify(_newSortedArray)) {
                return sortedSecondArray;
            }
            return prevOrder; // Return the previous state to prevent re-renders
        });
    }, [filterLabelsAndOptions, settings]);

    useEffect(() => {
        updateOrder();
    }, [updateOrder]); // Note: Reduce dependencies if possible

    useEffect(() => {
        const isFiltersApplied = !!Object.keys(memoizedFilters).length || false;

        if (isFiltersApplied) {
            const _applied = Object.keys(memoizedFilters).length;
            setIsResetDisabled(false);
            setAppliedFiltersCount(_applied);
        } else {
            setIsResetDisabled(true);
            setAppliedFiltersCount(0);
        }
    }, [memoizedFilters]);

    const handleFilterValueChange = (event, questionLabel) => {
        const label = questionLabel;
        const value = event.target.value.toString();
        const checked = event.target.checked;
        const datum = { ...selectedFilters };

        let array = datum[label] || [];
        const index = array.indexOf(value);

        if (checked && index === -1) {
            array = [...array, value];
        } else {
            array = array.filter((item) => item !== value);
        }

        datum[label] = [...array];

        if (array.length === 0) {
            delete datum[label];
        }

        //remove if apply button is implemented
        dispatch(loadCurrentFilterState(datum));
    };

    const handleReset = () => {
        dispatch(loadCurrentFilterState({}));
    };

    const handleSelectAllFilterValue = (event, filterKey) => {
        const col = filterKey;
        const checked = event.target.checked;
        const datum = { ...selectedFilters };

        let array = [];
        if (checked) {
            array = filterLabelsAndOptions[col] || [];
        } else {
            array = [];
        }

        datum[col] = [...array];

        if (array.length === 0) {
            delete datum[col];
        }
        //update
        dispatch(loadCurrentFilterState(datum));
    };

    const returnSelectAllCheckboxes = (ele) => {
        return (
            <FormControlLabel
                key={`${ele.toString()}-dashboard-filter-label`}
                sx={{
                    color: selectedFilters?.[ele]?.length === filterLabelsAndOptions?.[ele]?.length || false ? theme.palette.primary.main : theme.palette.text.primary,
                    background: selectedFilters?.[ele]?.length === filterLabelsAndOptions?.[ele]?.length || false ? "linear-gradient(270deg, rgba(0,103,239,0) 0%, rgba(0,94,239,0.06907777759541311) 78%)" : "none",
                    borderRadius: "10px",
                    width: "100%",
                    margin: "3px -10px",
                    padding: "0px",
                }}
                control={
                    <Checkbox
                        key={`${ele}-${"all"}-check`}
                        checked={selectedFilters?.[ele]?.length === filterLabelsAndOptions?.[ele]?.length || false}
                        size="small"
                        name={ele}
                        onChange={(event) => handleSelectAllFilterValue(event, ele)}
                    />
                }
                label={
                    <span
                        key={`${ele}-label`}
                        style={{
                            fontSize: "0.75rem",
                            marginBottom: 0,
                            marginLeft: "5px",
                        }}
                    >
                        Select All
                    </span>
                }
            />
        );
    };

    const returnCheckboxes = (ele, option) => {
        let productCodeWithSr;
        if (ele === "product_codes") {
            const spilitArr = option.split("*/*");
            if (spilitArr.length > 1) {
                let pcTitle = spilitArr[0];
                if (isOralCare) {
                    const [pc, testCode] = pcTitle.split("/");
                    productCodeWithSr = pc + `/<span style='color: #6f42c1;'>${testCode}</span>`;
                } else {
                    productCodeWithSr = `${pcTitle} / ${spilitArr[1]}`;
                }
            }
        }
        return (
            <FormControlLabel
                key={`${option.toString()}-dashboard-filter-label`}
                className="dashboard-filter-label"
                sx={{
                    color: selectedFilters?.[ele]?.includes(option) ? theme.palette.primary.main : theme.palette.text.primary,
                    background: selectedFilters?.[ele]?.includes(option) ? "linear-gradient(270deg, rgba(0,103,239,0) 0%, rgba(0,94,239,0.06907777759541311) 78%)" : "none",
                    borderRadius: "10px",
                    width: "100%",
                    margin: "3px 0px",
                    padding: "0px",
                }}
                control={
                    <Checkbox
                        key={`${ele}-${option}-check`}
                        checked={selectedFilters?.[ele]?.includes(option) || false}
                        size="small"
                        name={option}
                        value={option}
                        // sx={{
                        //     padding: 0,
                        // }}
                        onChange={(event) => handleFilterValueChange(event, ele)}
                    />
                }
                label={
                    <span
                        key={`${option}-label`}
                        style={{ fontSize: "0.75rem" }}
                    >
                        {ele === "product_codes" ? <span dangerouslySetInnerHTML={{ __html: productCodeWithSr }} /> : option}
                    </span>
                }
            />
        );
    };

    return (
        <>
            <div
                key={"fil-1"}
                style={{
                    // margin: "0px 0px 10px",
                    marginTop: "10px",
                    marginBottom: "10px",
                    border: "1px solid #00000020",
                    background: "#f7f7f7",
                    padding: "12px 10px",
                    color: "#000000ab",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "0.75rem",
                }}
            >
                <span key={"fil-1-span"}>{appliedFiltersCount ? `${appliedFiltersCount} filters selected` : `No filters selected`}</span>
                <Button
                    key={"fil-1-button"}
                    size="small"
                    variant="outlined"
                    onClick={handleReset}
                    disabled={isResetDisabled}
                    sx={{
                        color: "#000000ab",
                        outline: "#000000ab",
                        fontSize: "10px",
                    }}
                >
                    Reset All
                </Button>
            </div>
            <div
                key={"fil-2-fi"}
                className="filter-section"
                style={{
                    fontSize: "14px",
                    color: "#000",
                    minHeight: `calc(100vh - ${height + 135}px)`,
                    maxHeight: `calc(100vh - ${height + 135}px)`,
                    overflowY: "auto",
                    marginBottom: "10px",
                    borderBottom: "1px solid #00000020",
                    borderTop: "1px solid #00000020",
                }}
            >
                {filterLabelsAndOptions &&
                    filterOrder
                        .filter((bv) => backendVaribleToExclude.indexOf(bv) === -1)
                        ?.map((ele, index) => {
                            return (
                                <Accordion
                                    // defaultExpanded
                                    key={`fil-2-accordion-panel-${index}-header-${index}`}
                                    sx={{
                                        background: "#dbdbdb2b",
                                    }}
                                >
                                    <AccordionSummary
                                        aria-controls={`panel-${index}-content`}
                                        id={`panel-${index}-header`}
                                    >
                                        <Typography
                                            className="text-xs"
                                            style={{
                                                fontSize: "0.75rem",
                                            }}
                                            sx={{
                                                margin: 0,
                                                paddingLeft: "4px",
                                            }}
                                        >
                                            {filtersLabelMap?.[ele] || ele}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        sx={{
                                            padding: "0px 10px",
                                        }}
                                    >
                                        {returnSelectAllCheckboxes(ele)}

                                        {filterLabelsAndOptions[ele].map((option) => {
                                            return (
                                                <div
                                                    key={option}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        padding: "0px 5px",
                                                    }}
                                                >
                                                    {returnCheckboxes(ele, option)}
                                                </div>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
            </div>
        </>
    );
}

// Typechecking props for the DashboardFilters
DashboardFilters.propTypes = {
    height: PropTypes.number,
    filterData: PropTypes.object,
    settings: PropTypes.array,
};

export default DashboardFilters;
